import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MermaidAPI, MarkdownComponent } from 'ngx-markdown';
import {
    ButtonComponent,
    IconComponent,
    InlineNotificationComponent,
    ToastService,
    TooltipDirective,
} from '@pure/hive';
import { RichBlockActionActivation, RichBlockSegment } from '@pure1/data';
import {
    FeedbackState,
    getFullVariationText,
    IChatFeedbackRequest,
    IConversationMessage,
    MessageFeedback,
    THUMBS_DOWN,
    THUMBS_UP,
} from '../model/conversation';
import { ReasoningStepsComponent } from './reasoning-steps/reasoning-steps.component';
import { CollapsibleSectionsComponent } from './collapsible-sections/collapsible-sections.component';
import { ResponseSelectorComponent } from './response-selector/response-selector.component';
import { MessageFeedbackComponent } from './message-feedback/message-feedback.component';
import { RichBlockComponent } from './rich-blocks/rich-block.component';
import { RichBlockSkeletonComponent } from './rich-blocks/rich-block-skeleton/rich-block-skeleton.component';
import { MermaidLoaderService } from './mermaid-loader.service';
import { faRetweet, faThumbsUp, faThumbsDown, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';

const REGENERATION_COUNT_LIMIT = 4;

@Component({
    selector: 'copilot-message',
    standalone: true,
    imports: [
        InlineNotificationComponent,
        MarkdownComponent,
        TooltipDirective,
        ButtonComponent,
        ReasoningStepsComponent,
        CollapsibleSectionsComponent,
        ResponseSelectorComponent,
        MessageFeedbackComponent,
        IconComponent,
        RichBlockComponent,
        RichBlockSkeletonComponent,
    ],
    templateUrl: './message.component.html',
    styleUrl: './message.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageComponent {
    @Input() message!: IConversationMessage;
    @Input() conversationSessionId!: string;
    // Needed to show additional actions and markdown on the latest response
    @Input() isLatestResponse!: boolean;

    @Input() longQueryInProgress: boolean = false;
    @Input() isGenerating: boolean = false;

    // Feedback status - handled by parent for now but may move to component later
    @Input() isFeedbackLoading: boolean = false;
    @Input() isFeedbackSuccessful: boolean = false;
    @Input() isFeedbackFailed: boolean = false;
    @Input() showFeedback: boolean = false;
    @Output() closeAdditionalFeedback: EventEmitter<FeedbackState> = new EventEmitter();
    @Output() submitAdditionalFeedback: EventEmitter<IChatFeedbackRequest> = new EventEmitter();

    @Output() setVariation: EventEmitter<{
        message: IConversationMessage;
        variation: number;
    }> = new EventEmitter();
    @Output() submitFeedback: EventEmitter<{
        feedback: MessageFeedback;
        message: IConversationMessage;
    }> = new EventEmitter();
    @Output() regenerate: EventEmitter<IConversationMessage> = new EventEmitter();
    /**
     * A message to send as an ordinary chat turn, from a rich block's prompt action (FR-016).
     *
     * The same shape and the same contract as `copilot-follow-up-bubble`'s output of the same
     * name: the container owns the conversation and calls `submitMessage`, exactly as it does
     * for a follow-up chip. This component stays a renderer.
     */
    @Output() sendMessage: EventEmitter<string> = new EventEmitter();

    protected readonly faSparkles = faSparkles;
    protected readonly THUMBS_UP = THUMBS_UP;
    protected readonly THUMBS_DOWN = THUMBS_DOWN;
    protected readonly regenerationCountLimit = REGENERATION_COUNT_LIMIT;
    protected readonly mermaidOptions: MermaidAPI.MermaidConfig = {
        fontFamily: '"Inter", sans-serif',
        logLevel: 'error',
        theme: 'default',
        themeVariables: {
            pieTitleTextSize: '16px',
            pieSectionTextSize: '14px',
            pieLegendTextSize: '14px',
        },
        suppressErrorRendering: true,
    };

    private readonly mermaidLoader = inject(MermaidLoaderService);
    // ngx-markdown throws if [mermaid] is on before the lazily-loaded mermaid is on the window.
    protected readonly mermaidLoaded = this.mermaidLoader.loaded;

    constructor(private readonly toast: ToastService) {
        void this.mermaidLoader.ensureLoaded();
    }

    clickCopyToClipboard(message: IConversationMessage): void {
        const variation = message.variations[message.selectedVariationIndex];
        navigator.clipboard
            .writeText(getFullVariationText(variation))
            .then(() => {
                this.toast.addToast({
                    message: 'Message copied',
                    severity: 'default',
                });
            })
            .catch((err: unknown) => {
                this.toast.addToast({
                    message: 'Failed to copy message',
                    severity: 'major',
                });

                console.error('Failed to copy to clipboard', err);
            });
    }

    /**
     * The selected answer as the runs the template walks: prose, block, prose (FR-007).
     *
     * Falls back to the whole message as one run when the variation carries no segments, which is
     * what keeps the block-free case byte-identical to what this rendered before.
     *
     * A fresh array each change detection is deliberate: `@for` tracks by index and `[data]`
     * compares by value, and the conversation is `cloneDeep`d on every chunk anyway, so
     * memoizing on the variation's identity would buy nothing.
     */
    answerSegments(message: IConversationMessage): RichBlockSegment[] {
        const variation = message.variations[message.selectedVariationIndex];
        return variation.richBlockSegments?.length
            ? variation.richBlockSegments
            : [{ kind: 'text', text: variation.message }];
    }

    /**
     * A user activated an action on a rich block — from a standalone `actions` block, from a
     * `callout`, or from a `task_list` step. All three arrive here.
     *
     * A prompt action is sent immediately as an ordinary chat message (FR-016): no confirmation,
     * no fill-the-box step, and nothing marking it button-originated. It goes out on the same
     * output a follow-up chip uses, so the container hands it to the same `submitMessage` — the
     * full query renders verbatim as the user's bubble, and the button's label stays here.
     */
    onRichBlockAction(activation: RichBlockActionActivation): void {
        if (activation.kind === 'prompt') {
            this.sendMessage.emit(activation.query);
            return;
        }

        // TODO (CLOUD-264010): resolve the target through the R4 allowlist and the permission
        // gate, then route. Rendered but inert until then — deliberately not a guessed route map.
    }

    isPlaceholderMessage(message: IConversationMessage): boolean {
        return message.author === 'ai' && message.variations[message.selectedVariationIndex].message === null;
    }

    showResponseSelector(message: IConversationMessage): boolean {
        return message.author === 'ai' && message.variations.length > 1 && this.isLatestResponse;
    }

    protected readonly faCopy = faCopy;
    protected readonly faRetweet = faRetweet;
    protected readonly faThumbsUp = faThumbsUp;
    protected readonly faThumbsDown = faThumbsDown;
}
